import * as Yup from 'yup';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { isEmail } from 'validator';
import { isValidObjective } from '../utils';

export interface StaffMember {
  id: string;
  name: string;
  email: string;
  profilePic: string;
}
export interface IMember {
  name: string;
  dob?: string;
  email: string;
  phoneNumber?: string;
}

export interface FormValues {
  firstName: string;
  lastName?: string;
  description?: string;
  dob?: string;
  pronoun?: string;
  email: string;
  phoneNumber?: string;
  objectives: string[];
  staffMembers?: StaffMember[];
}

export const initialValues: FormValues = {
  firstName: '',
  lastName: '',
  description: '',
  pronoun: '',
  email: '',
  objectives: [],
  staffMembers: [],
};

export interface IGroupClientFormValues {
  groupName: string;
  description?: string;
  objectives: string[];
  staffMembers: StaffMember[];
  members: IMember[];
}

export const groupClientInitialValues: IGroupClientFormValues = {
  groupName: '',
  description: '',
  objectives: [],
  staffMembers: [],
  members: [
    {
      name: '',
      dob: '',
      email: '',
      phoneNumber: '',
    },
  ],
};

const minDate = new Date();
minDate.setFullYear(minDate.getFullYear() - 100);

const isPhoneNumberValid = (value: string | undefined) => {
  if (value) {
    return isValidPhoneNumber(value);
  }
  return true;
};

const isEmailValid = (value: string | undefined) => {
  if (value) {
    return isEmail(value);
  }
  return true;
};

export const getValidationSchema = (isObjectivesTouched: boolean) =>
  Yup.object().shape({
    firstName: Yup.string()
      .required('addNewClient.validations.firstName')
      .max(50, 'addNewClient.validations.NameMax'),
    lastName: Yup.string().optional().max(50, 'addNewClient.validations.NameMax'),
    dob: Yup.date()
      .optional()
      .min(minDate, 'addNewClient.validations.dobMin')
      .max(new Date(), 'addNewClient.validations.dobMax')
      .typeError('addNewClient.validations.dobTypeError'),
    pronoun: Yup.string().optional(),
    email: Yup.string()
      .test('email', 'addNewClient.validations.emailInvalid', isEmailValid)
      .required('addNewClient.validations.emailRequired'),
    phoneNumber: Yup.string()
      .optional()
      .test('phoneNumber', 'addNewClient.validations.phoneNumberValid', isPhoneNumberValid),
    objectives: isObjectivesTouched
      ? Yup.array()
          .required('addNewClient.validations.objectives')
          .of(
            Yup.string()
              .max(50, 'addNewClient.isTagTooLong')
              .test('is-valid-objective', 'addNewClient.validations.objectivesInvalid', (value) =>
                isValidObjective(value || '')
              )
          )
          .min(1, 'addNewClient.empty')
          .max(6, 'addNewClient.max')
          .test('is-unique', 'Each objective must be unique', (value) => {
            const uniqueValues = new Set(value);
            return uniqueValues.size === value.length;
          })
      : Yup.array()
          .required('addNewClient.validations.objectives')
          .of(
            Yup.string()
              .max(50, 'addNewClient.isTagTooLong')
              .test('is-valid-objective', 'addNewClient.validations.objectivesInvalid', (value) =>
                isValidObjective(value || '')
              )
          )
          .min(1, 'addNewClient.empty')
          .max(6, 'addNewClient.max')
          .test('is-unique', 'Each objective must be unique', (value) => {
            const uniqueValues = new Set(value);
            return uniqueValues.size === value.length;
          }),
  });

export const getGroupValidationSchema = (isObjectivesTouched: boolean, step: number) => {
  const baseSchema = {
    groupName: Yup.string()
      .max(50, 'addNewClient.validations.NameMax')
      .required('addNewClient.validations.groupName'),
    description: Yup.string().optional().max(200, 'addNewClient.validations.NameMax'),
    objectives: isObjectivesTouched
      ? Yup.array()
          .required('addNewClient.validations.objectives')
          .of(
            Yup.string()
              .max(50, 'addNewClient.isTagTooLong')
              .test('is-valid-objective', 'addNewClient.validations.objectivesInvalid', (value) =>
                isValidObjective(value || '')
              )
          )
          .min(1, 'addNewClient.empty')
          .max(6, 'addNewClient.max')
          .test('is-unique', 'Each objective must be unique', (value) => {
            const uniqueValues = new Set(value);
            return uniqueValues.size === value.length;
          })
      : Yup.array()
          .required('addNewClient.validations.objectives')
          .of(
            Yup.string()
              .max(50, 'addNewClient.isTagTooLong')
              .test('is-valid-objective', 'addNewClient.validations.objectivesInvalid', (value) =>
                isValidObjective(value || '')
              )
          )
          .min(1, 'addNewClient.empty')
          .max(6, 'addNewClient.max')
          .test('is-unique', 'Each objective must be unique', (value) => {
            const uniqueValues = new Set(value);
            return uniqueValues.size === value.length;
          }),
  };

  if (step === 1) {
    baseSchema.members = Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string()
            .required('addNewClient.validations.firstName')
            .max(50, 'addNewClient.validations.NameMax'),
          dob: Yup.date()
            .optional()
            .min(minDate, 'addNewClient.validations.dobMin')
            .max(new Date(), 'addNewClient.validations.dobMax')
            .typeError('addNewClient.validations.dobTypeError'),
          email: Yup.string()
            .test('email', 'addNewClient.validations.emailInvalid', isEmailValid)
            .required('addNewClient.validations.emailRequired'),
          phoneNumber: Yup.string()
            .optional()
            .test('phoneNumber', 'addNewClient.validations.phoneNumberValid', isPhoneNumberValid),
        })
      )
      .min(1, 'addNewClient.validations.minMembers')
      .max(20, 'addNewClient.validations.maxMembers');
  }

  return Yup.object().shape(baseSchema);
};

export const clientDetailsFormValidatorSchema = () =>
  Yup.object().shape({
    firstName: Yup.string()
      .required('addNewClient.validations.firstName')
      .max(50, 'addNewClient.validations.NameMax'),
    description: Yup.string().optional().max(200, 'addNewClient.validations.NameMax').nullable(),
  });
